import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper';

export interface AssetMember {
  id?: number;
  member_id: number;
  member_name?: string;
  member_phone?: string;
  percentage: number;
}

export interface Asset {
  id: number;
  group_id: number;
  name: string;
  description?: string;
  total_value: number;
  purchase_date: string;
  status: 'active' | 'sold' | 'disposed';
  notes?: string;
  members?: AssetMember[];
  created_at?: string;
  updated_at?: string;
}

export interface AssetFilters {
  search?: string;
  status?: string;
  sort_field?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface Member {
  id: number;
  name: string;
  phone_number: string;
}

// Hook to fetch all assets
export const useAssets = (filters: AssetFilters = {}) => {
  return useQuery({
    queryKey: ['assets', filters],
    queryFn: async () => {
      const { data } = await api.get('/assets', { params: filters });
      return data;
    },
  });
};

// Hook to fetch a single asset
export const useAsset = (id: number) => {
  return useQuery({
    queryKey: ['asset', id],
    queryFn: async () => {
      const { data } = await api.get(`/assets/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

// Hook to fetch members for asset assignment
export const useAssetMembers = () => {
  return useQuery({
    queryKey: ['asset-members'],
    queryFn: async () => {
      const { data } = await api.get('/assets/members');
      return data;
    },
  });
};

// Hook to create a new asset
export const useCreateAsset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (asset: Omit<Asset, 'id' | 'group_id' | 'created_at' | 'updated_at'>) => {
      const { data } = await api.post('/assets', asset);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Asset created successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

// Hook to update an asset
export const useUpdateAsset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...asset }: Partial<Asset> & { id: number }) => {
      const { data } = await api.put(`/assets/${id}`, asset);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['asset'] });
      toast.success('Asset updated successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

// Hook to delete an asset
export const useDeleteAsset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/assets/${id}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      toast.success('Asset deleted successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

// Hook to export asset to Excel
export const useExportAssetExcel = () => {
  return useMutation({
    mutationFn: async (assetId: number) => {
      const response = await api.get(`/assets/${assetId}/export/excel`, {
        responseType: 'blob',
      });
      
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `asset_ownership_${assetId}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
};

// Hook to export asset to PDF
export const useExportAssetPdf = () => {
  return useMutation({
    mutationFn: async (assetId: number) => {
      const response = await api.get(`/assets/${assetId}/export/pdf`, {
        responseType: 'blob',
      });
      
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `asset_ownership_${assetId}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });
};

